package uz.mint.frontend.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.window
import org.w3c.dom.url.URLSearchParams
import org.jetbrains.compose.web.css.*
import uz.mint.frontend.api.AccountApi
import uz.mint.frontend.api.CustomerEsim
import uz.mint.frontend.api.CustomerOrder
import uz.mint.frontend.api.formatUzs
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.i18n.AccountStrings
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.accountStringsFor
import uz.mint.frontend.i18n.stringsFor
import uz.mint.frontend.toSitePalette

/**
 * One purchase in full — `/order?ref=M-AB12CD34`.
 *
 * The order history is a list of one-line rows, which is the right shape for scanning and the
 * wrong one for the question a customer actually arrives with: *where is my eSIM*. This is where
 * a row leads — the same eSIM card My eSIMs shows, QR and all, with the payment it came from
 * above it.
 *
 * **Both halves are read from the customer's own collections rather than from an order endpoint
 * that joins them.** `/api/me/orders` and `/api/me/esims` are already scoped to the session and
 * already carry everything on this page; a third endpoint would be a third place for the
 * ownership rule to be got wrong.
 */
@Composable
fun OrderDetailPage(locale: Locale) {
    val account = accountStringsFor(locale)

    AccountShell(locale, account.orderTitle) { _, _ ->
        OrderDetail(locale, account)
    }
}

@Composable
private fun OrderDetail(locale: Locale, account: AccountStrings) {
    val palette = ColorMode.current.toSitePalette()
    val strings = stringsFor(locale)

    // Read once per composition of this page: the address bar cannot change underneath it without
    // a navigation, which remounts the page anyway.
    val reference = remember { URLSearchParams(window.location.search).get("ref")?.trim().orEmpty() }

    var order by remember { mutableStateOf<CustomerOrder?>(null) }
    var esim by remember { mutableStateOf<CustomerEsim?>(null) }
    var loaded by remember { mutableStateOf(false) }

    LaunchedEffect(reference) {
        runCatching {
            val orders = AccountApi.orders()
            val esims = AccountApi.esims()
            // Case-insensitive: a reference typed by hand or forwarded through a cashier comes
            // back in whatever case that system chose.
            order = orders.firstOrNull { it.reference.equals(reference, ignoreCase = true) }
            esim = esims.firstOrNull { it.orderReference?.equals(reference, ignoreCase = true) == true }
        }
        loaded = true
    }

    val found = order
    when {
        !loaded -> SpanText(account.esimsLoading, Modifier.fontSize(15.px).color(palette.ink2))

        found == null -> AccountCard {
            SpanText(account.orderNotFound, Modifier.fontSize(15.px).lineHeight(1.5))
            BackLink(locale, account)
        }

        else -> Column(Modifier.fillMaxWidth().gap(20.px)) {
            AccountCard {
                SpanText(
                    found.planName,
                    Modifier.fontSize(22.px).fontWeight(FontWeight.ExtraBold).letterSpacing((-0.02).em),
                )

                Column(Modifier.fillMaxWidth().gap(2.px)) {
                    DetailRow(account.orderRefLabel, found.reference, mono = true)
                    DetailRow(account.orderDateLabel, formatDate(found.createdAt))
                    DetailRow(account.esimStatusLabel, account.orderStatus(found.status))
                    found.paymentMethod?.let { DetailRow(account.orderMethodLabel, paymentLabel(it)) }
                    DetailRow(
                        account.orderPriceLabel,
                        found.priceUzs?.let { formatUzs(it, strings) } ?: "—",
                        strong = true,
                    )
                }

                BackLink(locale, account)
            }

            SpanText(
                account.orderEsimTitle,
                Modifier.fontSize(13.px).fontWeight(FontWeight.Bold).letterSpacing(0.08.em)
                    .textTransform(TextTransform.Uppercase).color(palette.ink2),
            )

            val profile = esim
            if (profile != null) {
                SingleEsimCard(profile, account, locale)
            } else {
                // Paid but not yet provisioned, or provisioning failed and a human is on it. Either
                // way the customer needs a sentence, not an empty space where a QR should be.
                AccountCard {
                    SpanText(
                        account.orderEsimPending,
                        Modifier.fontSize(15.px).lineHeight(1.5).color(palette.ink2),
                    )
                }
            }
        }
    }
}

@Composable
private fun DetailRow(label: String, value: String, mono: Boolean = false, strong: Boolean = false) {
    val palette = ColorMode.current.toSitePalette()
    Row(
        Modifier.fillMaxWidth().gap(16.px).flexWrapCss()
            .padding(topBottom = 10.px)
            .borderBottom(2.px, LineStyle.Solid, palette.rule),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(label, Modifier.fontSize(14.px).color(palette.ink2))
        Spacer()
        SpanText(
            value,
            Modifier
                .fontSize(if (strong) 17.px else 15.px)
                .fontWeight(if (strong) FontWeight.ExtraBold else FontWeight.SemiBold)
                .textAlign(TextAlign.End)
                .then(
                    if (mono) Modifier.fontFamily("ui-monospace", "SFMono-Regular", "monospace")
                    else Modifier
                ),
        )
    }
}

@Composable
private fun BackLink(locale: Locale, account: AccountStrings) {
    val palette = ColorMode.current.toSitePalette()
    Link(
        path = locale.profilePath,
        modifier = Modifier.fontSize(14.px).fontWeight(FontWeight.Bold).color(palette.accentText),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    ) { SpanText("← ${account.orderBack}") }
}

/** `click` / `payme` as the cashiers spell themselves, not as our column stores them. */
private fun paymentLabel(method: String): String = when (method.lowercase()) {
    "click" -> "Click"
    "payme" -> "Payme"
    else -> method
}
